
import { Component, Vue } from 'vue-property-decorator';
import { DatePicker, Form, FormItem, Input, Option, Select, TimeSelect, Upload, Button } from 'element-ui';
import { mapGetters } from 'vuex';
import timezone from 'moment-timezone';
import { Concept as ConceptService, Messaging, Promotion, Item, Category, Order } from '@/services/SOLO';
import TabHeader from '@/views/Admin/pages/Messaging/components/TabHeader';
import { translations } from '@/mixins';
import { Feature_Flags } from '@/enums/globals';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface DataObject {
  selected_segment: string;
  title: any;
  message: any;
  is_pushed: boolean;
  schedule_date: any;
  schedule_time: any;
  deep_link: any;
  image: any;
}

interface SimpleItem {
  value: string;
  text: string;
}

@Component({
  computed: {
    ...mapGetters({
      activeConcept: 'account/activeConcept',
      segments: 'messaging/getSegments',
      conceptSettings: 'account/getConceptSettings',
      activeMenu: 'menu/getMenus',
      getLocale: 'app/getLocale',
    }),
    isNotificationDisabled () {
      const { isFeatureEnabled } = useFeatureFlags();
      return isFeatureEnabled(Feature_Flags.PORTAL_DISABLE_NOTIFICATION);
    },
  },
  components: {
    [DatePicker.name]: DatePicker,
    [TimeSelect.name]: TimeSelect,
    [Form.name]: Form,
    [Button.name]: Button,
    [FormItem.name]: FormItem,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    [Upload.name]: Upload,
    TabHeader,
  },
  filters: {
    formTitle (str: any) {
      return str.split('-').map((item: any) => {
        return item.charAt(0).toUpperCase() + item.substring(1);
      }).join(' ');
    },
    fieldTitle (str: any) {
      return str.split('-').map((item: any) => {
        return item.charAt(0).toUpperCase() + item.substring(1);
      }).join(' ');
    },
    capitalizeFirstLetter (str: any) {
      if (typeof str !== 'string') return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
  mixins: [translations],
})
export default class TabCompose extends Vue {
  $notify: any
  segments!: Array<any>;
  data: DataObject = {
    selected_segment: '',
    title: {},
    message: {},
    is_pushed: false,
    schedule_date: '',
    schedule_time: '',
    deep_link: '',
    image: '',
  };

  saving = false
  conceptSettings!: any;
  activeConcept!: any;
  activeMenu!: any;
  public translate!: Function
  options: SimpleItem[] = [
    { value: '', text: 'All Customers' },
  ];

  selectedSegment = '';
  link: any = ''
  target_link: any = ''
  target_link_item: any = ''
  image: any = ''
  link_url: any = ''
  image_url: any = ''
  menus: any = []
  link_options = [
    { key: 'Home', value: 'home' },
    { key: 'Category', value: 'category' },
    { key: 'Item', value: 'item' },
    { key: 'Digital Coupon', value: 'digital-coupon' },
    { key: 'Cart', value: 'shopping-cart' },
  ]

  target_menu: any = ''
  is_menu_loaded = false
  target_link_options: any = []
  event_select: any = ''
  link_selected: any = {}
  getLanguages!: Function;
  getConceptSettings!: any;
  arrayLang: Array<string> = []
  isUploadingImage = false;

  mounted () {
    this.getMenus();
    const primaryLanguage = this.getConceptSettings?.attributes?.['primary-language'];
    const secondaryLanguage = this.getConceptSettings?.attributes?.['secondary-language'];
    this.arrayLang = [primaryLanguage, secondaryLanguage];
    this.arrayLang.forEach((lang: string) => {
      this.data.title[lang] = '';
      this.data.message[lang] = '';
    });
  }

  onSendPushNotification () {
    this.saving = true;
    const r = confirm('Are you sure?');
    let scheduled_date = null;
    if (r) {
      const defaultTimezone = this.conceptSettings.attributes['default-timezone'] || 'GMT+3';
      const tz = `Etc/${defaultTimezone}`;
      const formData = new FormData();
      formData.append('segment_id', this.data.selected_segment);
      this.arrayLang.forEach((lang: string) => {
        formData.append(`title[${lang}]`, this.data.title[lang]);
        formData.append(`message[${lang}]`, this.data.message[lang]);
      });
      Object.keys(this.link_selected).forEach((key) => formData.append(`deep-link[${key}]`, this.link_selected[key]));

      if (this.data.is_pushed) {
        scheduled_date = this.data.schedule_date.toString().replace('00:00:00', `${this.data.schedule_time}:00`);
        scheduled_date = timezone.utc(scheduled_date).tz(tz).format('YYYY-MM-DD HH:mm:ss');
      } else {
        scheduled_date = '';
      }
      formData.append('scheduled_date', scheduled_date);
      formData.append('image-uri', this.image_url);

      Messaging.sendPushNotification(formData)
        .then(() => {
          /* @ts-ignore */
          analytics.track('send_push_notifications', {
            is_scheduled_notification: this.data.is_pushed,
          });
          alert('Notification was sent successfully.');
          this.saving = false;
          this.data.selected_segment = '';
          this.data.title = {};
          this.data.message = {};
          this.data.schedule_date = '';
          this.data.schedule_time = '';
          this.data.is_pushed = false;
          this.link_selected = {};
          this.link = '';
          this.target_menu = '';
          this.target_link = '';
        })
        .catch((error) => {
          this.saving = false;
          if (Array.isArray(error?.response?.data?.error)) {
            error.response.data.error = error.response.data.error[0];
          }

          this.$notify({
            title: this.translate('Error'),
            verticalAlign: 'bottom',
            horizontalAlign: 'left',
            message: this.translate(error.response.data.error.detail),
            type: 'danger',
            icon: 'fas fa-bomb',
          });
        });
    } else {
      this.saving = false;
    }
  }

  processLink (event: any) {
    this.target_menu = '';
    this.target_link = '';
    this.target_link_options = [];
    this.target_link_item = '';
    this.event_select = event;
    this.link_selected = {
      type: event,
    };

    this.link_url = this.event_select == 'home' || this.event_select == 'shopping-cart'
      ? `https://${this.activeConcept.key}.page.link/${this.event_select}`
      : '';
    this.data.deep_link = this.link_url;
    if (this.event_select === 'digital-coupon') {
      this.getLinkResource(this.event_select, this.target_menu);
    }
  }

  selectedMenu (event: any) {
    this.target_menu = event;
    this.is_menu_loaded = true;
    this.link_selected = { ...this.link_selected, menus: event };
    this.getLinkResource(this.event_select, this.target_menu);
  }

  getLinkResource (data: any, menu_id: any) {
    switch (data) {
    case 'item':
      Messaging.menuItem(menu_id).then((response) => {
        this.target_link_options = response.data.data;
      });
      break;
    case 'category':
      Category.all(menu_id).then((response) => {
        this.target_link_options = response.data.data;
      });
      break;
    case 'digital-coupon':
      Messaging.getDigitalCoupons().then((response) => {
        this.target_link_options = response.data.data.filter((a: any) => {
          return a.attributes.status === 'active';
        });
      });
      break;
    }
  }

  processLinkChild (event: any) {
    this.target_link_item = event;
    this.link_url = `https://${this.activeConcept.key}.page.link/${this.event_select}-${this.target_link_item}`;
    this.data.deep_link = this.link_url;
    this.link_selected = { ...this.link_selected, value: event };
  }

  async handleChange (file: any) {
    const imgForm = new FormData();
    imgForm.append('pn_image', file.raw);
    this.image = file.name;

    this.isUploadingImage = true;
    await Messaging.uploadToS3(imgForm).then((response: any) => {
      this.image_url = response.data;
      this.data.image = this.image_url;
    }).catch((err: any) => {
      this.$notify({
        title: this.translate('Error'),
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: this.translate(err.response.data.error.detail),
        type: 'danger',
        icon: 'fas fa-bomb',
      });
    });

    this.isUploadingImage = false;
  }

  getMenus () {
    Messaging.menus().then((response: any) => {
      this.menus = response.data.data;
    });
  }
}
